import type { ProviderNetworkCard, ProviderNetworkEdge } from 'src/api/redux';
import {
  CONNECTION_META,
  CONNECTION_TYPE_PRECEDENCE,
  EDGE_SUBTYPE_CONNECTION_TYPE,
  REASON_SAME_FACILITY,
  REASON_SAME_SPECIALTY,
  REASON_SAME_TRAINING,
  REASON_TOP_REFERRER,
  SHARED_ATTRIBUTES_LABEL,
  SHARED_PATIENTS_LABEL,
  TRAINING_EDGE_SUBTYPES,
  type SimilarProviderConnectionType,
} from './constants';
import type { SimilarProvider, SimilarProviderConnection } from './types';

/**
 * Maps an API edge to the connection type the carousel renders it as, or `null` for a
 * subtype we don't recognise (the API may add subtypes before the frontend does).
 */
const connectionTypeFor = (
  edge?: ProviderNetworkEdge | null
): SimilarProviderConnectionType | null =>
  EDGE_SUBTYPE_CONNECTION_TYPE[edge?.edge_subtype] ?? null;

/**
 * The card's connection pills, one per distinct (type, label) pair.
 *
 * Deduping matters because `TRAINED_WITH` and `IN_TRAINING` both render as "Trained
 * With" — a provider who is both at the same hospital would otherwise get two identical
 * pills. Two different programs still yield two pills, since the label differs.
 */
export const buildConnections = (
  edges?: ProviderNetworkEdge[] | null
): SimilarProviderConnection[] => {
  const byKey = new Map<string, SimilarProviderConnection>();

  (edges ?? []).forEach((edge) => {
    const type = connectionTypeFor(edge);
    if (!type) return;

    // entity_name is null on the subtypes with nothing to name, so fall back to the label.
    const label = edge?.entity_name || CONNECTION_META[type].label;
    const key = `${type}-${label}`;

    if (!byKey.has(key)) byKey.set(key, { type, label });
  });

  return Array.from(byKey.values());
};

/**
 * The strongest connection present, used for the accent bar.
 * Falls back to `patient_sharing` when nothing is recognised.
 */
export const getPrimaryConnection = (
  edges?: ProviderNetworkEdge[] | null
): SimilarProviderConnectionType => {
  const present = new Set(
    (edges ?? [])
      .map((edge) => connectionTypeFor(edge))
      .filter((type): type is SimilarProviderConnectionType => !!type)
  );

  return (
    CONNECTION_TYPE_PRECEDENCE.find((type) => present.has(type)) ??
    'patient_sharing'
  );
};

/**
 * The first training edge, dated or not. `event_start` is nullable on every edge, so
 * requiring one here would drop a real shared-training connection — the date is only a
 * requirement for the class-year metric, not for recognising the connection.
 */
const findTrainingEdge = (
  edges?: ProviderNetworkEdge[] | null
): ProviderNetworkEdge | undefined =>
  (edges ?? []).find((edge) =>
    TRAINING_EDGE_SUBTYPES.includes(edge?.edge_subtype)
  );

/** The first training edge carrying a real year — the only kind that yields a class year. */
const findDatedTrainingEdge = (
  edges?: ProviderNetworkEdge[] | null
): ProviderNetworkEdge | undefined =>
  (edges ?? []).find(
    (edge) =>
      TRAINING_EDGE_SUBTYPES.includes(edge?.edge_subtype) && !!edge?.event_start
  );

/**
 * Two-digit class year from a training edge's start date, e.g. '2002-01-01' -> "'02".
 * Returns `null` when the date is missing or unparseable.
 */
export const getClassYear = (eventStart?: string | null): string | null => {
  const year = String(eventStart ?? '').slice(0, 4);
  if (!/^\d{4}$/.test(year)) return null;

  return `'${year.slice(2)}`;
};

/**
 * The card's headline figure and its caption.
 *
 * Shared patients is the only quantified connection, so it wins when present. Training
 * connections show a class year instead. Anything else falls back to counting the
 * connections, which is all the API gives us to lead with.
 */
export const buildMetric = (
  card?: ProviderNetworkCard | null
): Pick<SimilarProvider, 'metric_value' | 'metric_label'> => {
  const edges = card?.edges ?? [];

  const sharingEdge = edges.find(
    (edge) => edge?.edge_subtype === 'PATIENT_SHARING'
  );
  if ((sharingEdge?.shared_patients ?? null) !== null) {
    return {
      metric_value: sharingEdge.shared_patients,
      metric_label: SHARED_PATIENTS_LABEL,
    };
  }

  const trainingEdge = findDatedTrainingEdge(edges);
  const classYear = getClassYear(trainingEdge?.event_start);
  if (classYear) {
    return {
      metric_value: classYear,
      metric_label: trainingEdge?.entity_name ?? null,
    };
  }

  const connectionCount = buildConnections(edges).length;
  return connectionCount
    ? {
        metric_value: connectionCount,
        metric_label: SHARED_ATTRIBUTES_LABEL,
      }
    : { metric_value: null, metric_label: null };
};

/**
 * Why this provider is on the profile's carousel, or `null` when we can't say.
 *
 * The API reports several facts that could each explain the connection but doesn't rank
 * them, so the order here is the frontend's call: the two the server computed outrank a
 * specialty match (which we derive), which outranks shared training. Only one reason is
 * shown, so the most specific available wins.
 *
 * There is no fallback on purpose. Every reason above points at something in the
 * response; a card with none of them is one the API ranked in without telling us why,
 * and asserting a match we can't evidence is worse than saying nothing — the caller
 * omits the line instead.
 */
export const buildReason = (
  card?: ProviderNetworkCard | null,
  profileSpecialty?: string | null
): string | null => {
  if (card?.referrals?.top_referring_provider) return REASON_TOP_REFERRER;
  if (card?.same_primary_address) return REASON_SAME_FACILITY;

  const specialty = card?.specialty?.trim()?.toLowerCase();
  if (specialty && specialty === profileSpecialty?.trim()?.toLowerCase()) {
    return REASON_SAME_SPECIALTY;
  }

  if (findTrainingEdge(card?.edges)) return REASON_SAME_TRAINING;

  return null;
};

/**
 * Adapts one API card into the view model the carousel renders.
 *
 * `profileSpecialty` is passed in rather than read from the card because the "same
 * specialty" reason compares against the profile being viewed, which isn't in the
 * response — which is also why this can't live in the endpoint's `transformResponse`.
 */
export const toSimilarProvider = (
  card: ProviderNetworkCard,
  profileSpecialty?: string | null
): SimilarProvider => ({
  provider_id: card?.provider_id,
  type: card?.type || 'hcp',
  name: card?.name,
  specialty: card?.specialty ?? null,
  city: card?.city ?? null,
  state: card?.state ?? null,
  primary_connection: getPrimaryConnection(card?.edges),
  ...buildMetric(card),
  reason: buildReason(card, profileSpecialty),
  connections: buildConnections(card?.edges),
});

/**
 * Adapts a whole response, dropping any card we can't key. The API already excludes
 * unresolvable providers, but a card with no id can't be linked to or added to a list.
 */
export const toSimilarProviders = (
  cards?: ProviderNetworkCard[] | null,
  profileSpecialty?: string | null
): SimilarProvider[] =>
  (cards ?? [])
    .filter((card) => !!card?.provider_id)
    .map((card) => toSimilarProvider(card, profileSpecialty));
